import React from 'react';
import { MockService, RecorderConfig, RecordingMapping } from '../../../../../../../types/rest/mockServiceTypes';
import { MockParameterState } from '../../../MockParameters';
import _ from 'lodash';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import RecordedMessages from './RecordedMessages';
import ComponentList from '../../../../../../../components/lists/ComponentList/ComponentList';
import MessagingMapping from '../ListRenderComponents/MessagingMapping';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../../../../../store/rootReducer';
import { updateMessagingRecordedTab } from '../../../../../../../store/mockServices/mockServiceDetail/mockServiceDetailActions';
import BasicRecordingSettings from './BasicRecordingSettings';
import { RecordingSettingButtons } from './RecordingSettingsButtons';

interface Props {
  mockService: MockService;
  onUpdate: (params: MockParameterState) => void;
  disabled: boolean;
}

const MessagingRecordingConfiguration = ({ mockService, onUpdate, disabled }: Props) => {
  const { recorderConfigDto } = mockService;
  const activeTab = useSelector((state: ApplicationState) => state.mockServiceDetails.recordingConfigTab);
  const dispatch = useDispatch();

  const getMappings = (): RecordingMapping[] => {
    return _.get(recorderConfigDto, 'mappings', []);
  };

  const getMaxMessageCount = () => {
    return _.get(recorderConfigDto, 'maxMessagesCount', 0);
  };

  const getMaxMessagesPerSecondCountCount = () => {
    return _.get(recorderConfigDto, 'maxMessagesPerSecondCount', 0);
  };

  const updateConfigHandler = (fieldName: keyof RecorderConfig, value: any) => {
    // Ensure deep copy and provide fallback if config is empty
    const recorderConfig: RecorderConfig = {
      ...recorderConfigDto,
      mappings: [...(recorderConfigDto?.mappings || [])],
    } || {
      maxMessagesCount: 0,
      maxMessagesPerSecondCount: 0,
      mappings: [],
    };

    if (fieldName === 'maxMessagesCount' || fieldName === 'maxMessagesPerSecondCount') {
      const parsedValue = parseInt(value);
      recorderConfig[fieldName] = isNaN(parsedValue) ? 0 : parsedValue;
    } else {
      recorderConfig[fieldName] = value;
    }

    // Update mock service
    const ms = { ...mockService, recorderConfigDto: recorderConfig };
    onUpdate(ms);
  };

  const getRecordingSettings = () => {
    return (
      <>
        <Row className={'mb-6'}>
          <Col md={4}>
            <BasicRecordingSettings
              disabled={disabled}
              maxMessageCount={getMaxMessageCount()}
              maxMessagesPerSecondCount={getMaxMessagesPerSecondCountCount()}
              updateConfigHandler={(key: any, value) => {
                updateConfigHandler(key, value);
              }}
            />
          </Col>
          <Col md={6}></Col>
        </Row>

        <h4>Bindings</h4>
        <p>
          Virtual Inbound and Virtual Outbound mapping in JMS recording allow users to capture and replay message flows
          efficiently.
        </p>

        <Row>
          <Col md={6}>
            <ComponentList
              disabled={disabled}
              emptyListMessage={'There are no Mappings defined'}
              list={getMappings()}
              onAddNewItem={() => {
                const mapping: RecordingMapping = {
                  inboundDestination: '',
                  outboundDestination: '',
                  originType: 'QUEUE',
                };
                const mappings = _.cloneDeep(getMappings());
                mappings.push(mapping);
                updateConfigHandler('mappings', mappings);
              }}
              renderItem={MessagingMapping}
              onListChange={mappings => {
                updateConfigHandler('mappings', mappings);
              }}
              label={'Binding Name'}
            />
          </Col>
        </Row>
      </>
    );
  };

  return (
    <div>
      <RecordingSettingButtons
        activeTab={activeTab}
        onTabChange={tab => {
          dispatch(updateMessagingRecordedTab(tab));
        }}
      />
      {activeTab === 'settings' && getRecordingSettings()}
      {activeTab === 'recordedMessages' && <RecordedMessages mockService={mockService} disabled={disabled} />}
    </div>
  );
};

export default MessagingRecordingConfiguration;
